import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { useMergePrismicPreviewData } from 'gatsby-plugin-prismic-previews';
import css from './Footer.module.scss';
import ExternalLink from '@components/ExternalLink/ExternalLink';
import Link from '@components/Link/Link';

import SymbolSharetribe from '@images/logos/SymbolSharetribe/SymbolSharetribe';
import LogoFacebook from '@images/logos/LogoFacebook/LogoFacebook';
import LogoTwitter from '@images/logos/LogoTwitter/LogoTwitter';
import LogoYoutube from '@images/logos/LogoYoutube/LogoYoutube';
import PrismicLink from '../PrismicLink/PrismicLink';
import handleEditCookieConsent from '@utils/handleEditCookieConsent';
import { PrismicRichText } from '@prismicio/react';

const groupIntoColumns = body => {
  const columns = Object.entries(
    body.reduce(function (r, a) {
      r[a.primary.column] = r[a.primary.column] || [];
      r[a.primary.column].push(a);
      return r;
    }, {})
  );

  return columns;
};
const Footer = () => {
  const staticData = useStaticQuery(graphql`
    query NewFooterQuery {
      prismicFooter {
        _previewable
        data {
          company_body {
            ... on PrismicFooterDataCompanyBodyFooterColumnGroup {
              id
              primary {
                column
                title
              }
              items {
                link {
                  url
                  target
                }
                text
              }
            }
          }
          product_body {
            ... on PrismicFooterDataProductBodyFooterColumnGroup {
              id
              primary {
                column
                title
              }
              items {
                link {
                  url
                  target
                }
                text
              }
            }
          }
          resources_body {
            ... on PrismicFooterDataResourcesBodyFooterColumnGroup {
              id
              primary {
                column
                title
              }
              items {
                link {
                  url
                  target
                }
                text
              }
            }
          }
          bottom_text
          bottom_links {
            text
            link {
              url
              target
            }
          }
          company_our_story {
            richText
          }
        }
      }
    }
  `);
  const {
    prismicFooter: { data },
  } = useMergePrismicPreviewData(staticData);

  return (
    <footer className={css.footer}>
      <div className={css.footerContainer}>
        <div className={css.branding}>
          <div className={css.logo}>
            <SymbolSharetribe />
          </div>
          <p>Created with passion, coffee and tea since 2011 in Finland and California.</p>
          <div className={css.socialIcons}>
            <ExternalLink
              href="https://www.facebook.com/Sharetribe/"
              aria-label="Link to Sharetribe's Facebook page"
            >
              <LogoFacebook />
            </ExternalLink>
            <ExternalLink
              href="https://twitter.com/Sharetribe"
              aria-label="Link to Sharetribe's Twitter page"
            >
              <LogoTwitter />
            </ExternalLink>
            <ExternalLink
              href="https://www.youtube.com/channel/UCtefWVq2uu4pHXaIsHlBFnw"
              aria-label="Link to Sharetribe's YouTube channel"
            >
              <LogoYoutube />
            </ExternalLink>
          </div>
        </div>
        <div className={css.sitemap}>
          <div className={css.sitemapBlock}>
            <p className={css.eyebrow}>Product</p>
            {groupIntoColumns(data.product_body).map(column => {
              return (
                <div>
                  {column[1].map(group => {
                    return (
                      <div key={group.id}>
                        <p className={css.main_font_h6}>{group.primary.title}</p>
                        <ul>
                          {group.items.map(item => {
                            return (
                              <li key={item.text}>
                                <PrismicLink to={item.link?.url} targetBlank={item.link.target}>
                                  {item.text}
                                </PrismicLink>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          <div className={css.sitemapBlock}>
            <p className={css.eyebrow}>Resources</p>
            {groupIntoColumns(data.resources_body).map(column => {
              return (
                <div>
                  {column[1].map(group => {
                    return (
                      <div key={group.id}>
                        <p className={css.main_font_h6}>{group.primary.title}</p>
                        <ul>
                          {group.items.map(item => {
                            return (
                              <li key={item.text}>
                                <PrismicLink to={item.link?.url} targetBlank={item.link.target}>
                                  {item.text}
                                </PrismicLink>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          <div className={css.sitemapBlock}>
            <p className={css.eyebrow}>Company</p>
            {groupIntoColumns(data.company_body).map(column => {
              return (
                <div>
                  {column[1].map(group => {
                    return (
                      <div key={group.id}>
                        <p className={css.main_font_h6}>{group.primary.title}</p>
                        <ul>
                          {group.items.map(item => {
                            return (
                              <li key={item.text}>
                                <PrismicLink to={item.link?.url} targetBlank={item.link.target}>
                                  {item.text}
                                </PrismicLink>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              );
            })}

            <div className={css.colspan}>
              <p className={css.main_font_h6}>Our story</p>
              <PrismicRichText field={data.company_our_story.richText} />

              <span className={css.storyArrow}>
                <Link to="/company">
                  Continue reading
                  <svg width="15" height="9.374" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="m14.725 5.35-3.75 3.75a.937.937 0 1 1-1.326-1.326L11.8 5.625H.937A.943.943 0 0 1 0 4.661c0-.545.42-.91.938-.91H11.8L9.65 1.6A.937.937 0 1 1 10.977.274l3.75 3.75a.938.938 0 0 1-.002 1.325Z"
                      fill="#FF5257"
                      fill-rule="nonzero"
                    />
                  </svg>
                </Link>
              </span>
            </div>
          </div>

          <hr />

          <div className={css.sitemapBlock}>
            <div className={css.copyright}>{data.bottom_text}</div>
            <div className={css.footerTinyLinks}>
              <ul>
                {data.bottom_links.map(item => {
                  return (
                    <li key={item.text}>
                      <PrismicLink to={item.link?.url} targetBlank={item.link.target}>
                        {item.text}
                      </PrismicLink>
                    </li>
                  );
                })}
                <li>
                  <a href="#" onClick={handleEditCookieConsent}>
                    Edit cookie consent
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
